import React from 'react';
import { CheckCircle, ErrorOutline, InfoOutlined, Groups, EventAvailable, Paid } from '@mui/icons-material';
import { BrandMark } from './AppShell';

// Presentation-only building blocks for the signed-out screens (login / forgot / reset).

export const AuthLayout: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex bg-slate-50">
    {/* Brand panel — desktop only */}
    <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative overflow-hidden bg-gradient-to-br from-mqbit-950 via-mqbit-900 to-mqbit-700 text-white p-12 flex-col justify-between">
      <div className="absolute -right-24 -bottom-24 w-96 h-96 bg-mqbit-500/25 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -left-16 top-1/3 w-72 h-72 bg-mqbit-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10">
        <BrandMark />
      </div>

      <div className="relative z-10 max-w-md">
        <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-medium text-mqbit-200 border border-solid border-white/10">
          MQBIT Technologies HR Portal
        </span>
        <h1 className="text-4xl font-extrabold mt-5 mb-0 leading-tight text-white">
          Your people, time &amp; payroll — in one place.
        </h1>
        <p className="text-mqbit-200 text-sm mt-4 mb-0 leading-relaxed">
          Managing AI engineering teams, project allocations, and employee operations seamlessly.
        </p>

        <div className="mt-10 space-y-4">
          {[
            { icon: <Groups sx={{ fontSize: 18 }} />, text: 'Team, projects & timesheets' },
            { icon: <EventAvailable sx={{ fontSize: 18 }} />, text: 'Attendance, leave & holidays' },
            { icon: <Paid sx={{ fontSize: 18 }} />, text: 'Payroll & payslips' },
          ].map((f) => (
            <div key={f.text} className="flex items-center gap-3 text-sm text-mqbit-100">
              <span className="w-9 h-9 rounded-xl bg-white/10 border border-solid border-white/10 flex items-center justify-center text-mqbit-300">
                {f.icon}
              </span>
              {f.text}
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 pt-6 border-0 border-t border-solid border-white/10 text-xs text-mqbit-300">
        © {new Date().getFullYear()} MQBIT Technologies · hrms.mqbit.in
      </div>
    </div>

    {/* Form panel */}
    <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-md">
        {/* Compact brand for mobile */}
        <div className="lg:hidden flex justify-center mb-6">
          <div className="bg-mqbit-950 rounded-2xl px-5 py-3 shadow-lg">
            <BrandMark />
          </div>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-solid border-slate-200 shadow-sm">
          <h2 className="m-0 text-2xl font-bold text-slate-900">{title}</h2>
          <p className="mt-1.5 mb-6 text-sm text-slate-500">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  </div>
);

interface AuthInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export const AuthInput: React.FC<AuthInputProps> = ({ label, id, ...rest }) => {
  const inputId = id || label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div>
      <label htmlFor={inputId} className="block text-xs font-semibold text-slate-700 mb-1.5">
        {label}
      </label>
      <input
        id={inputId}
        {...rest}
        className="w-full py-2.5 px-3.5 text-sm bg-slate-50 border border-solid border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-mqbit-500 focus:border-mqbit-500 focus:bg-white transition-all font-sans"
      />
    </div>
  );
};

const alertStyles = {
  error: { cls: 'bg-rose-50 text-rose-800 border-rose-200', icon: <ErrorOutline sx={{ fontSize: 18 }} /> },
  success: { cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: <CheckCircle sx={{ fontSize: 18 }} /> },
  info: { cls: 'bg-mqbit-50 text-mqbit-800 border-mqbit-200', icon: <InfoOutlined sx={{ fontSize: 18 }} /> },
};

export const AuthAlert: React.FC<{ severity: 'error' | 'success' | 'info'; children: React.ReactNode }> = ({ severity, children }) => (
  <div className={`flex gap-2.5 items-start px-4 py-3 mb-4 rounded-2xl border border-solid text-xs font-medium ${alertStyles[severity].cls}`}>
    <span className="flex shrink-0 mt-px">{alertStyles[severity].icon}</span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

export const PrimaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...rest }) => (
  <button
    {...rest}
    className={`w-full py-3 bg-mqbit-600 hover:bg-mqbit-700 text-white rounded-xl font-bold text-sm shadow-lg shadow-mqbit-500/30 border-0 cursor-pointer transition-all flex items-center justify-center gap-2 font-sans disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
  />
);

export const SecondaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...rest }) => (
  <button
    {...rest}
    className={`w-full py-2.5 bg-transparent hover:bg-mqbit-50 text-mqbit-700 rounded-xl font-semibold text-sm border-0 cursor-pointer transition-all font-sans ${className}`}
  />
);

export const OutlineButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...rest }) => (
  <button
    {...rest}
    className={`w-full py-2.5 bg-mqbit-50 hover:bg-mqbit-100 text-mqbit-700 rounded-xl font-bold text-sm border border-solid border-mqbit-200 cursor-pointer transition-all font-sans ${className}`}
  />
);
